import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { ChevronRight } from "lucide-react";
import { makeRequest } from "@/utils/makeRequest";
import { useQuery } from "@tanstack/react-query";
import type { Notification } from "@/interfaces/notification";
import { useAppSelector } from "@/store/hooks";
import { useMyNotifications, useMyWalletAndAssets, useProfile } from "@/hooks";

import Loader from "@/components/loader/Loader";

import { type FormattedTransactionInterface } from "@/interfaces/transaction";
import { ErrorState } from "@/components/ErrorState";
import type { WalletAsset } from "@/interfaces/asset";
import AssetItem from "@/components/shared/asset/AssetItem";
import WalletCard from "@/components/user/WalletCard";

export default function WalletPage() {
  const [selectedAsset, setSelectedAsset] = useState<WalletAsset>();

  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id;

  const {
    data: profile,
    isLoading,
    isError,
    refetch: refetchProfile,
  } = useProfile(userId as string);
  const { data: notifs } = useMyNotifications(userId as string);

  const {
    data: myWallet,
    isLoading: isLoadingWallet,
    isError: isErrorWallet,
    refetch,
  } = useMyWalletAndAssets(userId!);

  const userAssets: WalletAsset[] = myWallet?.assets || [];

  useEffect(() => {
    if (userAssets.length > 0 && !selectedAsset) {
      setSelectedAsset(userAssets[0]);
    }
  }, [userAssets, selectedAsset]);

  const getMyNotifications = async () => {
    const res = await makeRequest.get("/notification/me");
    return res.data;
  };

  const fetchRecentTransactions = async () => {
    const res = await makeRequest.get("/transaction/me", {
      params: {
        page: 1,
        limit: 5,
      },
    });

    return res.data;
  };

  const getUnreadCount = async () => {
    const res = await makeRequest.get("/notification/unread/count");
    return res.data;
  };

  const {
    data: myNotifs = [],
    isLoading: isLoadingNotifs,
    isError: isErrorNotifications,
    refetch: refetchNotifications,
  } = useQuery<Notification[]>({
    queryKey: ["my-notifications", userId],
    queryFn: getMyNotifications,
    enabled: !!userId,
  });

  const { data: unreadCount } = useQuery({
    queryKey: ["unread-count", userId],
    queryFn: getUnreadCount,
    enabled: !!userId,
  });

  const { data, isLoading: isLoadingTransactions } =
    useQuery<FormattedTransactionInterface>({
      queryKey: ["recent-transactions", userId],
      queryFn: fetchRecentTransactions,
      enabled: !!userId,
    });

  const assets: WalletAsset[] = myWallet?.assets || [];

  const filteredAssets = useMemo(() => {
    return assets.sort((a, b) => {
      const balanceDiff =
        Number(b.convertedBalance ?? 0) - Number(a.convertedBalance ?? 0);

      if (balanceDiff !== 0) return balanceDiff;

      return (a.token?.name ?? "").localeCompare(b.token?.name ?? "");
    });
  }, [assets]);

  if (
    isLoading ||
    isLoadingTransactions ||
    isLoadingNotifs ||
    isLoadingWallet
  ) {
    return <Loader type="bounce" />;
  }

  if (isError || isErrorNotifications || isErrorWallet) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
          refetchNotifications();
          refetchProfile();
        }}
      />
    );
  }

  return (
    <div className="space-y-6 mt-10">
      {/* Main Content Grid */}
      <div className="flex flex-col gap-2 w-full">
        <WalletCard />

        <div className="flex items-center justify-between mt-8 mx-2 font-semibold">
          <span>My Assets</span>
          <Button
            onClick={() => navigate("/dashboard/assets")}
            className="h-10 rounded-full font-medium cursor-pointer"
          >
            View more
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {filteredAssets?.slice(0, 10).map((asset) => (
            <AssetItem key={asset.id} asset={asset} />
          ))}
        </div>
      </div>
    </div>
  );
}
